import { Icon, Toggle, Typography } from "@/components/atoms";
import { useLayout, useOperatorAccess } from "@/hooks";
import { useAppDispatch } from "@/redux/hooks";
import { setViewAsProvider } from "@/redux/slices/faView";
import { persistor } from "@/redux/store";
import { clsx } from "@/utils";

/**
 * Banner shown to FA (RideMinder internal) staff on any provider. It switches between full
 * RideMinder-staff access and the operator's own subscription-scoped view ("View as operator").
 *
 * Toggling persists the choice and reloads. On reload the providers fetch sends the
 * `x-rm-view-as-operator` header (see baseQuery), web-app-v2 recomputes the provider permissions
 * accordingly, and the CASL ability + `isUnsubscribed` gating rebuild from that fresh response — so
 * the whole portal (menu, pages, lock pane) re-gates to match the chosen view.
 */
export const FaViewAsProviderBanner = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { isFa, viewAsProvider } = useOperatorAccess();
  const dispatch = useAppDispatch();
  const {
    layout: { sidebarOpen },
  } = useLayout();

  if (!isFa) return null;

  const handleToggle = async (checked: boolean) => {
    dispatch(setViewAsProvider(checked));
    // Flush the persisted flag before reloading so the fresh /providers fetch picks it up.
    await persistor.flush();
    window.location.reload();
  };

  return (
    <div
      className={clsx(
        "relative top-0 z-40 flex min-h-16 flex-col items-center justify-center gap-x-5 gap-y-1 bg-primary text-white lg:min-h-10 lg:flex-row lg:pl-[120px]",
        { "lg:pl-[270px]": sidebarOpen },
        className
      )}
      {...props}
    >
      <div className="mx-2 flex flex-row items-center gap-x-2">
        <Icon name="Eye" variant="Bold" size="xs" className="flex-shrink-0" />
        <Typography variant="paragraph">
          {viewAsProvider
            ? "Previewing the operator's own view — what they see with their subscription."
            : "Viewing with full RideMinder staff access."}
        </Typography>
      </div>
      <div className="mx-2 flex flex-row items-center gap-x-2.5">
        <Typography variant="paragraph">View as operator</Typography>
        <Toggle size="sm" checked={viewAsProvider} onChange={(checked) => void handleToggle(checked)} />
      </div>
    </div>
  );
};
